/**
 * Shared bits for the CAD ribbon: keytip badge overlay, tooltip
 * composition ("label\n description\n Command: NAME" — the OCS ribbon
 * tooltip model) and panel-button openers (Faz 5 palette targets).
 */

import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { RibbonEntryDef } from '../../../config/ribbonTabs';
import { CANONICAL_BY_ID } from '../../../core/commands/canonicalCommands';
import { useAppStore } from '../../../state/store';

/** Alt-chain badge: a tiny boxed letter at the button's corner. */
export function KeytipBadge({ letter, show }: { letter?: string; show: boolean }) {
    if (!show || !letter) return null;
    return (
        <Box
            as="span"
            position="absolute"
            bottom="-1px"
            right="-1px"
            px="3px"
            border="1px solid"
            borderColor="border.base"
            bg="bg.active"
            color="text.primary"
            fontSize="8px"
            lineHeight="10px"
            borderRadius="2px"
            pointerEvents="none"
            zIndex={1}
        >
            {letter}
        </Box>
    );
}

/** Localized label for a ribbon item (ribbon.items.<key>). */
export function useItemLabel(itemKey: string): string {
    return useTKey()(`ribbon.items.${itemKey}`);
}

/**
 * Dynamic-key translate: config-driven keys are plain strings at compile
 * time, so they go through this cast (the i18next literal-union typing
 * guards only the static keys). Options pass through unchanged.
 */
export function useTKey(): (key: string, opts?: Record<string, unknown>) => string {
    const { t } = useTranslation();
    return (key, opts) => String(t(key as never, (opts ?? {}) as never));
}

/** Primary command-line spelling for the tooltip tail. */
function primaryHint(cmdId: string): string {
    const cmd = CANONICAL_BY_ID.get(cmdId);
    return cmd?.names['2d'][0] ?? cmd?.names['3d'][0] ?? cmdId.toUpperCase();
}

/** Static English hints for shell actions (no canonical description). */
const SHELL_HINTS: Record<string, string> = {
    'doc.new': 'Create a new project document',
    'doc.open': 'Open an existing project document',
    'doc.save': 'Save the current project',
    'doc.saveAs': 'Save the current project under a new name',
    'doc.saveAsTemplate': 'Save the current project as a template',
    'edit.cut': 'Cut the selection to the clipboard',
    'edit.copy': 'Copy the selection to the clipboard',
    'edit.paste': 'Paste the clipboard content',
    'app.undo': 'Undo the last operation',
    'app.redo': 'Redo the last undone operation',
};

/** Tooltip body: label + description + command hint (400ms delay upstream). */
export function useEntryTooltip(entry: RibbonEntryDef): string {
    const label = useItemLabel(entry.key);
    const lines = [label];
    if (entry.cmdId) {
        const cmd = CANONICAL_BY_ID.get(entry.cmdId);
        if (cmd?.description) lines.push(cmd.description);
        lines.push(`Command: ${primaryHint(entry.cmdId)}`);
    } else if (entry.shell) {
        lines.push(SHELL_HINTS[entry.shell] ?? '');
    } else if (entry.tool) {
        lines.push(`Tool: ${entry.tool}`);
    }
    return lines.filter((l) => l.length > 0).join('\n');
}

/**
 * Palette openers for panelButton items (Faz 5): `layers` opens the Layer
 * Properties Manager palette (OCS Layer Properties button semantics),
 * `properties` the Properties panel, `blocks`/`xref`/`count` their Faz 5
 * palettes, `quickProperties` the floating mini palette. All toggle the
 * app-store flags the dock bridge mirrors bidirectionally.
 */
const PANEL_OPENERS: Record<string, { available: boolean; run: () => void }> = {
    properties: {
        available: true,
        run: () => {
            const app = useAppStore.getState();
            if (!app.propertiesPanelOpen) app.togglePropertiesPanel();
        },
    },
    layers: {
        available: true,
        run: () => {
            useAppStore.getState().toggleLayerManager();
        },
    },
    layerManager: {
        available: true,
        run: () => {
            useAppStore.getState().toggleLayerManager();
        },
    },
    blocks: {
        available: true,
        run: () => {
            useAppStore.getState().toggleBlocksPalette();
        },
    },
    xref: {
        available: true,
        run: () => {
            useAppStore.getState().toggleXrefPalette();
        },
    },
    count: {
        available: true,
        run: () => {
            useAppStore.getState().toggleCountPalette();
        },
    },
    quickProperties: {
        available: true,
        run: () => {
            useAppStore.getState().toggleQuickProperties();
        },
    },
};

export function panelButtonState(panelId: string): { available: boolean; run: () => void } {
    return PANEL_OPENERS[panelId] ?? { available: false, run: () => undefined };
}
